/**
 * Token Manager Service
 * ============================================================================
 *
 * Purpose:
 * --------
 * Manages authentication tokens using functional programming principles.
 * Provides token storage, retrieval, validation, and expiry checking.
 *
 * This service provides:
 * - Token storage (localStorage/sessionStorage)
 * - Token retrieval
 * - Token validation
 * - Expiry checking
 * - Token decoding
 * - Auto-refresh detection
 *
 * Design Principles:
 * ------------------
 * - **Functional programming** (no classes!)
 * - Pure functions where possible
 * - Immutable operations
 * - Type-safe
 * - Secure token handling
 *
 * Usage:
 * ------
 * ```typescript
 * import { getAccessToken, setTokens, isTokenValid } from 'core/auth';
 *
 * const token = getAccessToken();
 * const isValid = isTokenValid(token);
 * ```
 * ============================================================================
 */

import type { AuthTokens, TokenPayload } from './types';
import { AUTH_STORAGE_KEYS, TOKEN_EXPIRY, SESSION_CONFIG } from './constants';

/**
 * Decode JWT token
 *
 * Decodes JWT token without verification (verification happens server-side).
 *
 * @param token - JWT token string
 * @returns Decoded token payload or null if invalid
 */
export const decodeToken = (token: string): TokenPayload | null => {
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as TokenPayload;
  } catch (error) {
    console.warn('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 *
 * Pure function to check if a token has expired.
 *
 * @param token - JWT token string
 * @returns true if expired, false otherwise
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;

  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;

  const now = Date.now() / 1000; // Convert to seconds
  return payload.exp < now;
};

/**
 * Get token expiry timestamp
 *
 * @param token - JWT token string
 * @returns Expiry timestamp in milliseconds or null
 */
export const getTokenExpiry = (token: string | null): number | null => {
  if (!token) return null;

  const payload = decodeToken(token);
  if (!payload || !payload.exp) return null;

  return payload.exp * 1000; // Convert to milliseconds
};

/**
 * Get time until token expiry
 *
 * @param token - JWT token string
 * @returns Time in milliseconds until expiry, or 0 if expired/invalid
 */
export const getTimeUntilExpiry = (token: string | null): number => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return 0;

  const now = Date.now();
  const timeLeft = expiry - now;
  return Math.max(0, timeLeft);
};

/**
 * Check if token should be refreshed
 *
 * Pure function to determine if token should be refreshed based on expiry.
 *
 * @param token - JWT token string
 * @param refreshThreshold - Time before expiry to refresh (milliseconds)
 * @returns true if token should be refreshed
 */
export const shouldRefreshToken = (
  token: string | null,
  refreshThreshold: number = SESSION_CONFIG.AUTO_REFRESH_TIME
): boolean => {
  if (!token) return false;

  const timeUntilExpiry = getTimeUntilExpiry(token);
  return timeUntilExpiry > 0 && timeUntilExpiry <= refreshThreshold;
};

/**
 * Validate token format
 *
 * Pure function to validate JWT token format.
 *
 * @param token - Token string
 * @returns true if token format is valid
 */
export const isTokenFormatValid = (token: string | null): boolean => {
  if (!token) return false;

  // JWT should have 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) return false;

  // Each part should be non-empty
  return parts.every((part) => part.length > 0);
};

/**
 * Validate token
 *
 * Checks if token is valid (format + not expired).
 *
 * @param token - JWT token string
 * @returns true if token is valid and not expired
 */
export const isTokenValid = (token: string | null): boolean => {
  if (!isTokenFormatValid(token)) return false;
  return !isTokenExpired(token);
};

/**
 * Get storage based on remember me flag
 *
 * @param rememberMe - Whether to use localStorage (true) or sessionStorage (false)
 * @returns Storage object
 */
const getStorage = (rememberMe: boolean = false): Storage => {
  return rememberMe ? localStorage : sessionStorage;
};

/**
 * Get remember me flag from storage
 *
 * @returns Remember me flag
 */
export const getRememberMe = (): boolean => {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME);
    return stored === 'true';
  } catch {
    return false;
  }
};

/**
 * Set remember me flag
 *
 * @param rememberMe - Remember me flag
 */
export const setRememberMe = (rememberMe: boolean): void => {
  try {
    localStorage.setItem(AUTH_STORAGE_KEYS.REMEMBER_ME, String(rememberMe));
  } catch (error) {
    console.warn('Failed to set remember me:', error);
  }
};

/**
 * Get access token from storage
 *
 * @returns Access token or null
 */
export const getAccessToken = (): string | null => {
  try {
    const rememberMe = getRememberMe();
    const storage = getStorage(rememberMe);
    return storage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  } catch (error) {
    console.warn('Failed to get access token:', error);
    return null;
  }
};

/**
 * Get refresh token from storage
 *
 * @returns Refresh token or null
 */
export const getRefreshToken = (): string | null => {
  try {
    const rememberMe = getRememberMe();
    const storage = getStorage(rememberMe);
    return storage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  } catch (error) {
    console.warn('Failed to get refresh token:', error);
    return null;
  }
};

/**
 * Get tokens from storage
 *
 * @returns Auth tokens or null
 */
export const getTokens = (): AuthTokens | null => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (!accessToken || !refreshToken) return null;

  const expiresAt = getTokenExpiry(accessToken);

  return {
    accessToken,
    refreshToken,
    expiresAt: expiresAt || undefined,
    tokenType: 'Bearer',
  };
};

/**
 * Set tokens in storage
 *
 * @param tokens - Auth tokens
 * @param rememberMe - Whether to persist in localStorage
 */
export const setTokens = (tokens: AuthTokens, rememberMe: boolean = false): void => {
  try {
    setRememberMe(rememberMe);
    const storage = getStorage(rememberMe);

    storage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    storage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);

    // Store expiry if provided
    if (tokens.expiresAt) {
      storage.setItem(AUTH_STORAGE_KEYS.SESSION_EXPIRY, String(tokens.expiresAt));
    } else {
      // Calculate expiry from token
      const expiry = getTokenExpiry(tokens.accessToken);
      if (expiry) {
        storage.setItem(AUTH_STORAGE_KEYS.SESSION_EXPIRY, String(expiry));
      }
    }
  } catch (error) {
    console.error('Failed to set tokens:', error);
  }
};

/**
 * Clear tokens from storage
 *
 * Removes all auth-related data from storage.
 */
export const clearTokens = (): void => {
  try {
    // Clear from both storages to be safe
    const storages = [localStorage, sessionStorage];

    storages.forEach((storage) => {
      storage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
      storage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
      storage.removeItem(AUTH_STORAGE_KEYS.SESSION_EXPIRY);
      storage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
      storage.removeItem(AUTH_STORAGE_KEYS.AUTH_STATE);
    });

    localStorage.removeItem(AUTH_STORAGE_KEYS.REMEMBER_ME);
    localStorage.removeItem(AUTH_STORAGE_KEYS.LAST_LOGIN);
  } catch (error) {
    console.error('Failed to clear tokens:', error);
  }
};

/**
 * Check if access token is expired
 *
 * @returns true if expired
 */
export const isAccessTokenExpired = (): boolean => {
  const token = getAccessToken();
  return isTokenExpired(token);
};

/**
 * Check if refresh token is expired
 *
 * @returns true if expired
 */
export const isRefreshTokenExpired = (): boolean => {
  const token = getRefreshToken();
  return isTokenExpired(token);
};

/**
 * Check if tokens should be refreshed
 *
 * @returns true if tokens should be refreshed
 */
export const shouldRefresh = (): boolean => {
  const accessToken = getAccessToken();
  return shouldRefreshToken(accessToken);
};

/**
 * Get session expiry from storage
 *
 * @returns Session expiry timestamp or null
 */
export const getSessionExpiry = (): number | null => {
  try {
    const rememberMe = getRememberMe();
    const storage = getStorage(rememberMe);
    const expiry = storage.getItem(AUTH_STORAGE_KEYS.SESSION_EXPIRY);
    return expiry ? parseInt(expiry, 10) : null;
  } catch {
    return null;
  }
};

/**
 * Check if session is expired
 *
 * @returns true if session expired
 */
export const isSessionExpired = (): boolean => {
  const expiry = getSessionExpiry();
  if (!expiry) return true;

  return Date.now() >= expiry;
};

/**
 * Get last login timestamp
 *
 * @returns Last login timestamp or null
 */
export const getLastLogin = (): number | null => {
  try {
    const lastLogin = localStorage.getItem(AUTH_STORAGE_KEYS.LAST_LOGIN);
    return lastLogin ? parseInt(lastLogin, 10) : null;
  } catch {
    return null;
  }
};

/**
 * Set last login timestamp
 *
 * @param timestamp - Login timestamp (default: now)
 */
export const setLastLogin = (timestamp: number = Date.now()): void => {
  try {
    localStorage.setItem(AUTH_STORAGE_KEYS.LAST_LOGIN, String(timestamp));
  } catch (error) {
    console.warn('Failed to set last login:', error);
  }
};

/**
 * Create token manager instance
 *
 * Returns an object with all token management functions.
 */
export const createTokenManager = () => ({
  getAccessToken,
  getRefreshToken,
  getTokens,
  setTokens,
  clearTokens,
  isAccessTokenExpired,
  isRefreshTokenExpired,
  isTokenExpired,
  getTokenExpiry,
  decodeToken,
  isTokenValid,
  shouldRefresh,
  getSessionExpiry,
  isSessionExpired,
  getRememberMe,
  setRememberMe,
  getLastLogin,
  setLastLogin,
  getTimeUntilExpiry,
  shouldRefreshToken,
  isTokenFormatValid,
});

/**
 * Default token manager instance
 */
export const tokenManager = createTokenManager();
